/**
 * Job Data Adapter
 *
 * Transforms Supabase `job` table rows into the shape the frontend expects.
 * This is the bridge between database schema (snake_case, UUIDs) and
 * frontend components (camelCase, integer IDs).
 *
 * When adding new job fields to the database, add the mapping here.
 * The frontend components never touch raw database rows directly.
 */

import { supabase, getAuthUserId, requireAuthUserId } from '../lib/supabase';
import { resolveAssignmentRate } from '../utils/projectMetrics';

// ============================================================
// STAFFING SYNC
// ============================================================

/**
 * Recompute and update job.current_staffed from live assignment counts.
 * Call after any assignment INSERT, DELETE, or status change.
 *
 * @param {string} jobId
 * @returns {number} updated count
 */
export async function syncCurrentStaffed(jobId) {
  if (!jobId) return 0;
  const { count } = await supabase
    .from('assignment')
    .select('id', { count: 'exact', head: true })
    .eq('job_id', jobId)
    .in('status', ['Active', 'Onboarding']);

  const staffed = count || 0;
  await supabase.from('job').update({ current_staffed: staffed }).eq('id', jobId);
  return staffed;
}

// ============================================================
// CONSTANTS
// ============================================================

const STATUS_MAP = {
  'Open': 'open',
  'Closing Soon': 'closing_soon',
  'Active': 'active',
  'Completed': 'completed',
  'Draft': 'draft',
  'Cancelled': 'cancelled',
};

// ============================================================
// TRANSFORMERS
// ============================================================

/**
 * Transform a database job row into frontend job object
 */
function transformJob(row) {
  return {
    // Identity
    id: row.id,  // UUID from database
    _legacyId: row.id,  // Keep for backwards compat
    title: row.title,

    // Classification
    domain: row.domain,
    tier: row.tier,
    tags: row.tags || [],

    // Client (hidden from UI but kept in data)
    client: row.client_display_name || row.client_name || '',
    clientId: row.client_id,
    isConfidential: row.is_confidential,

    // Details
    briefDescription: row.brief_description,
    overview: row.overview,
    responsibilities: row.responsibilities || [],
    requirements: row.requirements || [],
    duration: row.duration,
    startDate: row.start_date ? new Date(row.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null,
    endDate: row.end_date ? new Date(row.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null,
    teamSize: row.team_size,
    hoursPerWeek: row.hours_per_week,
    posted: row.posted_at ? getRelativeTime(row.posted_at) : row.published_at ? getRelativeTime(row.published_at) : null,

    // Compensation
    rate: row.base_rate,
    rateRange: row.rate_min && row.rate_max ? { min: row.rate_min, max: row.rate_max } : null,
    referralBonus: row.referral_bonus,

    // Status
    status: STATUS_MAP[row.status] || row.status?.toLowerCase(),

    // Staffing
    hiredThisMonth: row.hired_this_month || 0,
    instantMatch: row.instant_match_enabled || false,
    instantMatchCriteria: row.instant_match_criteria || {},
    currentStaffed: row.current_staffed || 0,
    needsSpecialAssessment: row.needs_special_assessment || false,
    requiredAssessments: row._requiredAssessments || row.required_assessments || [],

    // Match (computed — for now use stored value or default)
    match: row.match_score || 0,
    matchReason: row.match_reason || '',

    // Progress (for active/completed jobs)
    progress: row.annotations_completed && row.annotations_target ? {
      completed: row.annotations_completed,
      target: row.annotations_target,
      percentage: row.completion_percentage || (row.annotations_completed / row.annotations_target * 100),
      quality: row.quality_average,
      hoursLogged: row.hours_logged_total,
      daysRemaining: row.days_remaining,
      onTrack: row.on_track,
    } : null,

    // Results (for completed jobs)
    results: row.final_quality ? {
      quality: row.final_quality,
      totalAnnotations: row.total_annotations_delivered,
      totalHours: row.total_hours_logged,
      clientSatisfaction: row.client_satisfaction,
    } : null,

    // Rich detail content
    details: row.what_you_do ? {
      whatYouDo: row.what_you_do || [],
      sampleTask: row.sample_task,
      tools: row.tools,
      schedule: row.schedule,
      qualityExpectations: row.quality_expectations,
      postedDate: row.posted_at ? new Date(row.posted_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null,
    } : null,
  };
}

/**
 * Transform a database application row into frontend application object
 */
function transformApplication(row) {
  return {
    id: row.id,
    jobId: row.job_id,
    jobTitle: row.job?.title || '',
    domain: row.job?.domain || '',
    tier: row.job?.tier || 'Standard',
    rate: row.job?.base_rate || 0,
    client: row.job?.client_display_name || '',
    teamSize: row.job?.team_size || 0,
    currentStaffed: row.job?.current_staffed || 0,
    overview: row.job?.overview || '',
    appliedDate: new Date(row.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    stage: mapStage(row.stage),
    stageLabel: row.stage,
    estimatedResponse: row.estimated_response_time || '2-3 days',
    source: row.source,
    decision: row.decision,
    // Steps
    resumeSubmitted: row.resume_submitted,
    domainAssessmentPassed: row.domain_assessment_passed,
    skillsEvaluationPassed: row.skills_evaluation_passed,
    workAuthVerified: row.work_auth_verified,
    completedSteps: row.completed_steps,
  };
}

/**
 * Transform a database transaction row into frontend transaction object
 */
function _transformTransaction(row) {
  return {
    id: row.id,
    date: new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    description: row.description,
    hours: row.hours,
    rate: row.base_rate,
    boost: row.boost_applied ? `$${row.boost_rate?.toFixed(1)}` : null,
    total: row.amount,
    status: row.status,
    type: row.type,
  };
}

/**
 * Transform a database assessment_definition row
 */
function transformAssessment(row, results = []) {
  const userResult = results.find(r => r.assessment_id === row.id);
  const isPassed = userResult?.passed;
  const domainLabel = row.domain || 'General';
  return {
    id: row.id,
    assessmentId: row.id,  // Used by checkRequiredAssessments to match against job_required_assessment
    name: row.name,
    domain: row.domain,
    type: row.type?.toLowerCase() || 'domain',
    description: row.description,
    format: row.format,
    duration: row.duration_minutes ? `${row.duration_minutes} min` : null,
    maxAttempts: row.max_attempts,
    passingScore: row.passing_score,
    validityMonths: row.validity_months,
    isActive: row.is_active,
    // Frontend-compatible fields
    status: isPassed ? 'passed' : userResult ? 'failed' : 'available',
    score: userResult?.score || null,
    userScore: userResult?.score || null,
    attempts: userResult?.attempt_number || 0,
    dateTaken: userResult?.taken_at ? new Date(userResult.taken_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null,
    expiresAt: userResult?.expires_at || null,  // ISO string for reliable date comparison in qualificationCheck
    expiresAtDisplay: userResult?.expires_at ? new Date(userResult.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null,
    unlocks: row.jobs_unlocked || 0,
    unlocksLabel: row.jobs_unlocked ? `${row.jobs_unlocked} ${domainLabel} jobs currently available` : null,
    jobsUnlocked: row.jobs_unlocked || 0,
  };
}

/**
 * Transform a database assignment row (for current jobs).
 *
 * EXPORTED for regression testing (R11 LOW-2 / BLOCKER-1 prevention): tests
 * verify that the `rate` field is always canonical (via resolveAssignmentRate)
 * so we never regress to raw `row.rate` and break downstream annotator views.
 */
export function transformAssignment(row) {
  // R10 HIGH-5 audit fix (ROOT CAUSE): jobAdapter is the source of
  // camelCase assignment shape consumed by AnnotatorDashboard, Jobs,
  // Profile, and every annotator-facing view. Previously used
  // `confirmed_rate || rate || 0` — legacy pattern that violates
  // R4 LOW-5 (legitimate rate=0 overridden by confirmed_rate). Now
  // routes through canonical resolveAssignmentRate — fixes downstream
  // transitively.
  const rate = resolveAssignmentRate(row);
  const hoursLogged = row.hours_logged || 0;
  const computedEarnings = hoursLogged * rate;
  // Use DB total_earned only when HIGHER than computed (indicates bonuses/manual adjustments).
  // When lower, hours_logged was updated by trigger but total_earned wasn't — computed is correct.
  // R12 LOW-1 audit fix: tolerance reduced from $1.00 to $0.01 (matches NUMERIC(12,2)
  // column precision). Prior `+ 1` discarded any DB bonus under $1 (e.g., a $0.50
  // referral credit) and showed the annotator the computed hours×rate instead.
  const totalEarned = row.total_earned && row.total_earned > computedEarnings + 0.01
    ? row.total_earned : computedEarnings;
  return {
    id: row.id,
    jobId: row.job_id,
    job: row.job ? transformJob(row.job) : null,
    status: row.status,
    assignedAt: row.assigned_at,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    quality: row.quality_score,
    annotationsCompleted: row.annotations_completed,
    hoursLogged,
    // R11 BLOCKER-1 audit fix: expose the CANONICAL rate (resolved via
    // resolveAssignmentRate above) as `rate` so downstream consumers
    // see `a.rate` = canonical. The R10 attempt resolved `rate` locally
    // for `totalEarned` but left the OUTPUT field as raw `row.rate` —
    // every downstream view reading `a.rate` got the raw null. Use the
    // already-resolved `rate` const instead.
    rate,
    // `confirmedRate` preserves the raw DB field for admin-facing
    // surfaces that need "what was confirmed vs raw" (e.g., Admin.jsx
    // pay-confirmation UI) — intentionally un-resolved.
    confirmedRate: row.confirmed_rate,
    payConfirmed: row.pay_confirmed || false,
    totalEarned,
    spl: row.spl ? { id: row.spl.id, name: row.spl.name, email: row.spl.email } : null,
  };
}

function formatRewardLabel(row) {
  switch (row.type) {
    case 'CashBonus':
      return row.amount ? `$${Math.round(row.amount)} Cash Bonus` : 'Cash Bonus';
    case 'RateBoost':
      return row.boost_percentage ? `${Math.round(row.boost_percentage)}% Hourly Rate Boost` : 'Hourly Rate Boost';
    case 'PriorityAccess':
      return 'Priority Access';
    case 'Spotlight':
      return 'Contributor Spotlight';
    case 'FacilitatorBonus':
      return row.amount ? `$${Math.round(row.amount)} Facilitator Bonus` : 'Facilitator Bonus';
    case 'ReferralBonus':
      return row.amount ? `$${Math.round(row.amount)} Referral Bonus` : 'Referral Bonus';
    default:
      return row.type || 'Reward';
  }
}

function mapRewardType(type) {
  switch (type) {
    case 'CashBonus':
      return 'cash_bonus';
    case 'RateBoost':
      return 'rate_boost';
    case 'PriorityAccess':
      return 'priority_access';
    case 'Spotlight':
      return 'spotlight';
    case 'FacilitatorBonus':
      return 'facilitator_bonus';
    case 'ReferralBonus':
      return 'referral_bonus';
    default:
      return (type || '').toLowerCase();
  }
}

function transformReward(row) {
  const type = mapRewardType(row.type);
  const category = ['cash_bonus', 'rate_boost', 'facilitator_bonus'].includes(type) ? 'team' : 'individual';
  const displayDate = row.used_at || row.activated_at || row.earned_at;

  return {
    id: row.id,
    type,
    category,
    label: formatRewardLabel(row),
    sprint: row.sprint?.name || row.source_type || 'Reward',
    team: row.team?.name || null,
    date: displayDate
      ? new Date(displayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null,
    status: row.status,
    amount: row.amount,
    hoursTotal: row.total_active_hours || null,
    hoursUsed: row.hours_used || 0,
    hoursRemaining: row.hours_remaining ?? null,
    boostPercentage: row.boost_percentage || null,
    description: row.description || null,
  };
}

/**
 * Transform a database referral row
 */
function transformReferral(row) {
  return {
    id: row.id,
    referralCode: row.referral_code,
    status: row.status,
    invitedAt: row.invited_at,
    signedUpAt: row.signed_up_at,
    assessmentPassedAt: row.assessment_passed_at,
    firstJobCompletedAt: row.first_job_completed_at,
    bonusPaidAt: row.bonus_paid_at,
    referrerBonus: row.referrer_bonus_amount,
    refereeBonus: row.referee_bonus_amount,
    // Join data
    refereeName: row.referee?.name || null,
    refereeDomain: row.referee?.primary_domain || null,
  };
}

// ============================================================
// FETCH FUNCTIONS
// ============================================================

/**
 * Fetch all open/closing soon jobs for Browse Jobs tab
 */
export async function fetchBrowseJobs() {
  const { data, error } = await supabase
    .from('job')
    .select('*')
    .in('status', ['Open', 'Closing Soon'])
    .order('published_at', { ascending: false });

  if (error) {
    console.error('Error fetching browse jobs:', error);
    return [];
  }

  // Compute current_staffed + hydrate required assessments
  const jobIds = data.map(j => j.id);
  if (jobIds.length > 0) {
    const [{ data: assignCounts }, { data: reqAssessments }] = await Promise.all([
      supabase.from('assignment').select('job_id').in('job_id', jobIds).in('status', ['Active', 'Onboarding']),
      supabase.from('job_required_assessment').select('job_id, assessment_definition_id, is_required').in('job_id', jobIds),
    ]);

    if (assignCounts) {
      const countMap = {};
      assignCounts.forEach(a => { countMap[a.job_id] = (countMap[a.job_id] || 0) + 1; });
      data.forEach(j => { j.current_staffed = countMap[j.id] || 0; });
    }

    // Attach required assessments from junction table to each job
    if (reqAssessments) {
      const reqMap = {};
      reqAssessments.forEach(r => {
        if (!reqMap[r.job_id]) reqMap[r.job_id] = [];
        reqMap[r.job_id].push({ assessmentDefinitionId: r.assessment_definition_id, isRequired: r.is_required });
      });
      data.forEach(j => { j._requiredAssessments = reqMap[j.id] || []; });
    }
  }

  return data.map(transformJob);
}

/**
 * Fetch current user's active assignments for Current Jobs tab
 */
export async function fetchCurrentJobs() {
  const userId = getAuthUserId();
  if (!userId) return { onboarding: [], active: [], completed: [] };

  const { data, error } = await supabase
    .from('assignment')
    .select('*, job(*)')
    .eq('individual_id', userId)
    .in('status', ['Onboarding', 'Active', 'Completed'])
    .order('assigned_at', { ascending: false });

  if (error) {
    console.error('Error fetching current jobs:', error);
    return { onboarding: [], active: [], completed: [] };
  }

  // Separate fetch for SPL contact info (managed_by is not a defined FK, so we can't embed it).
  // RLS may hide rows the annotator can't read — that's fine, we just won't have the PM contact.
  const splIds = [...new Set(data.map(r => r.managed_by).filter(Boolean))];
  let splMap = {};
  if (splIds.length > 0) {
    const { data: spls } = await supabase
      .from('individual')
      .select('id, name, email')
      .in('id', splIds);
    if (spls) for (const s of spls) splMap[s.id] = s;
  }
  const rows = data.map(r => ({ ...r, spl: splMap[r.managed_by] || null }));

  const assignments = rows.map(transformAssignment);
  return {
    onboarding: assignments.filter(a => a.status === 'Onboarding'),
    active: assignments.filter(a => a.status === 'Active'),
    completed: assignments.filter(a => a.status === 'Completed'),
  };
}

/**
 * Enhanced version of fetchCurrentJobs that also returns this week's time entries.
 * Used by AnnotatorDashboard for inline hour logging + weekly summary.
 */
export async function fetchCurrentJobsWithTimeEntries() {
  const userId = getAuthUserId();
  if (!userId) return { onboarding: [], active: [], completed: [], todayEntries: {}, weekEntries: {} };

  const { data, error } = await supabase
    .from('assignment')
    .select('*, job(*)')
    .eq('individual_id', userId)
    .in('status', ['Onboarding', 'Active', 'Completed'])
    .order('assigned_at', { ascending: false });

  if (error) {
    console.error('Error fetching current jobs:', error);
    return { onboarding: [], active: [], completed: [], todayEntries: {}, weekEntries: {} };
  }

  // Separate fetch for SPL contact info (managed_by is not a defined FK in PostgREST schema)
  const splIds = [...new Set(data.map(r => r.managed_by).filter(Boolean))];
  let splMap = {};
  if (splIds.length > 0) {
    const { data: spls } = await supabase
      .from('individual')
      .select('id, name, email')
      .in('id', splIds);
    if (spls) for (const s of spls) splMap[s.id] = s;
  }

  // Fetch this week's time entries (Sunday → Saturday)
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const weekStartStr = `${weekStart.getFullYear()}-${pad(weekStart.getMonth() + 1)}-${pad(weekStart.getDate())}`;

  const { data: timeData } = await supabase
    .from('time_entry')
    .select('assignment_id, date, hours, items_completed, note, status')
    .eq('individual_id', userId)
    .gte('date', weekStartStr)
    .lte('date', today)
    .order('date', { ascending: true });

  // Group time entries by assignment
  const weekEntries = {};
  const todayEntries = {};
  for (const entry of (timeData || [])) {
    const key = entry.assignment_id;
    if (!weekEntries[key]) weekEntries[key] = [];
    weekEntries[key].push(entry);
    if (entry.date === today) todayEntries[key] = entry;
  }

  const assignments = data.map(r => transformAssignment({ ...r, spl: splMap[r.managed_by] || null }));
  return {
    onboarding: assignments.filter(a => a.status === 'Onboarding'),
    active: assignments.filter(a => a.status === 'Active'),
    completed: assignments.filter(a => a.status === 'Completed'),
    todayEntries,
    weekEntries,
  };
}

/**
 * Fetch current user's rewards for worker-home and profile surfaces.
 */
export async function fetchRewards() {
  const userId = getAuthUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('reward')
    .select('*, sprint:sprint_id(name), team:team_id(name)')
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });

  if (error) {
    console.error('Error fetching rewards:', error);
    return [];
  }

  return data.map(transformReward);
}

/**
 * Fetch current user's applications for My Applications tab
 */
export async function fetchMyApplications() {
  const userId = getAuthUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('application')
    .select('*, job(title, domain, tier, base_rate, client_display_name, team_size, current_staffed, overview)')
    .eq('applicant_id', userId)
    .order('applied_at', { ascending: false });

  if (error) {
    console.error('Error fetching applications:', error);
    return [];
  }

  return data.map(transformApplication);
}

/**
 * Fetch assessments with user results for Assessments tab
 */
export async function fetchAssessments() {
  const userId = getAuthUserId();
  if (!userId) return { passed: [], available: [], all: [] };

  const [defsRes, resultsRes] = await Promise.all([
    supabase.from('assessment_definition').select('*').eq('is_active', true),
    supabase.from('assessment_result').select('*').eq('user_id', userId),
  ]);

  if (defsRes.error || resultsRes.error) {
    console.error('Error fetching assessments:', defsRes.error || resultsRes.error);
    return { passed: [], available: [] };
  }

  const all = defsRes.data.map(d => transformAssessment(d, resultsRes.data));
  return {
    passed: all.filter(a => a.status === 'passed'),
    available: all.filter(a => a.status !== 'passed'),
    all,
  };
}

/**
 * Fetch transactions and earnings for Payments tab.
 * Returns shape matching the UI's `earningsOverview` + `transactions` format exactly.
 */
export async function fetchPayments() {
  const userId = getAuthUserId();
  if (!userId) return { earningsOverview: null, transactions: [] };

  const { data: { user } } = await supabase.auth.getUser();
  // Phase 11 slot 8 (2026-04-24): drop user_metadata fallback per migration 055
  // (RLS no longer trusts user_metadata; client should mirror).
  const authOrgId = user?.app_metadata?.org_id || null;

  // Post-Phase-2 (migration 213 + audit): transaction.org_id and payout.org_id
  // are NOT NULL. The pre-migration `org_id.is.null OR eq` OR-fallback was
  // kept for rows that might have drifted nullable; that branch is now dead.
  // Straight equality is correct and strictly more selective — lets the
  // idx_transaction_org_status_date / idx_payout_org_user_status indexes
  // serve the query efficiently.
  //
  // The .select() string also pulls `job.title` so the UI can render the
  // project name directly instead of the raw ledger description (Finding
  // #6 from Phase 2 audit, fixed alongside migration 215's description
  // rewrite).
  let transactionsQuery = supabase.from('transaction')
    .select('*, job:job_id(id, title, domain)')
    .eq('user_id', userId);
  let payoutsQuery = supabase.from('payout').select('*').eq('user_id', userId);
  if (authOrgId) {
    transactionsQuery = transactionsQuery.eq('org_id', authOrgId);
    payoutsQuery = payoutsQuery.eq('org_id', authOrgId);
  }

  const [txRes, payoutRes, userRes] = await Promise.all([
    transactionsQuery.order('date', { ascending: false }),
    payoutsQuery.order('scheduled_date', { ascending: false }).limit(1),
    supabase.from('individual').select('lifetime_earnings, bonus_earnings, boosted_hours_earnings, total_hours_worked, total_jobs_completed, payment_method_last4').eq('id', userId).single(),
  ]);

  if (txRes.error) {
    console.error('Error fetching transactions:', txRes.error);
    return { earningsOverview: null, transactions: [] };
  }

  // Transform transactions to match UI field names exactly.
  //
  // Category mapping (post-Phase-2 audit):
  //   'ProjectEarnings', 'BoostedEarnings' → 'project'
  //   'SprintBonus', 'FacilitatorBonus', 'ReferralBonus' → 'bonus'
  //   'Reversal' → 'reversal' (negative amount, visually distinct in UI)
  //
  // The 'job' field shows the project title (from the joined job row),
  // falling back to description only for legacy rows that predate the FK.
  const transactions = txRes.data.map(row => {
    let category;
    if (row.type === 'Reversal') category = 'reversal';
    else if (['SprintBonus', 'FacilitatorBonus', 'ReferralBonus'].includes(row.type)) category = 'bonus';
    else category = 'project';

    return {
      id: row.id,
      _rawDate: row.date,
      date: new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      job: row.job?.title || row.description, // prefer joined job title; fall back to description
      jobDomain: row.job?.domain || null,
      description: row.description,
      hours: row.hours,
      baseRate: row.base_rate,
      boostApplied: row.boost_applied,
      boostRate: row.boost_rate,
      total: row.amount,              // may be negative for reversals
      status: row.status?.toLowerCase(),
      type: category,
      rawType: row.type,              // preserved for UI badge rendering
    };
  });

  // Compute period earnings (last 14 days) — use raw ISO dates for reliable filtering
  const now = new Date();
  const periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14);
  const periodEnd = now;
  const periodTx = transactions.filter(t => {
    const d = new Date(t._rawDate);
    return d >= periodStart;
  });

  const periodTotal = periodTx.reduce((sum, t) => sum + t.total, 0);
  const periodBase = periodTx.filter(t => t.type === 'project').reduce((sum, t) => sum + t.total, 0);
  const periodBonuses = periodTx.filter(t => t.type === 'bonus').reduce((sum, t) => sum + t.total, 0);
  const periodBoosted = periodTx.filter(t => t.boostApplied).reduce((sum, t) => sum + (t.total - (t.hours || 0) * (t.baseRate || 0)), 0);

  // Compute weekly trend from transactions (last 7 weeks)
  const weeklyTrend = [];
  for (let w = 6; w >= 0; w--) {
    const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (w + 1) * 7);
    const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - w * 7);
    const weekTotal = transactions.filter(t => {
      const d = new Date(t._rawDate);
      return d >= weekStart && d < weekEnd;
    }).reduce((sum, t) => sum + t.total, 0);
    weeklyTrend.push(Math.round(weekTotal));
  }

  // Build earningsOverview in exact UI shape
  const earningsOverview = {
    thisPeriod: {
      total: periodTotal,
      base: periodBase,
      bonuses: periodBonuses,
      boostedHours: periodBoosted,
      periodStart: periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      periodEnd: periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    },
    nextPayout: payoutRes.data?.[0] ? {
      amount: payoutRes.data[0].amount,
      date: new Date(payoutRes.data[0].scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      method: payoutRes.data[0].method,
      accountLast4: userRes.data?.payment_method_last4 || null,
    } : { amount: 0, date: '—', method: '—', accountLast4: '—' },
    lifetime: {
      // Compute from transactions (source of truth), not stale individual fields
      // Use ?? instead of || to avoid 0 falling through to stale individual fields
      total: transactions.length > 0 ? transactions.reduce((s, t) => s + t.total, 0) : (userRes.data?.lifetime_earnings ?? 0),
      bonuses: transactions.length > 0 ? transactions.filter(t => t.type === 'bonus').reduce((s, t) => s + t.total, 0) : (userRes.data?.bonus_earnings ?? 0),
      boostedHours: userRes.data?.boosted_hours_earnings ?? 0,
      hoursWorked: transactions.length > 0 ? transactions.filter(t => t.hours > 0).reduce((s, t) => s + t.hours, 0) : (userRes.data?.total_hours_worked ?? 0),
      jobsCompleted: userRes.data?.total_jobs_completed || 0,
    },
    weeklyTrend,
  };

  return { earningsOverview, transactions };
}

/**
 * Fetch referrals for Referrals tab
 */
export async function fetchReferrals() {
  const userId = getAuthUserId();
  if (!userId) return { referrals: [], code: null, totalEarnings: 0 };

  const { data: { user } } = await supabase.auth.getUser();
  // Phase 11 slot 8: drop user_metadata fallback per migration 055.
  const authOrgId = user?.app_metadata?.org_id || null;

  let referralQuery = supabase
    .from('referral')
    .select('*, referee:referee_id(name, primary_domain)')
    .eq('referrer_id', userId);

  if (authOrgId) {
    referralQuery = referralQuery.or(`org_id.is.null,org_id.eq.${authOrgId}`);
  }

  const [refRes, userRes] = await Promise.all([
    referralQuery,
    supabase.from('individual').select('referral_code, referral_code_hash, total_referral_earnings').eq('id', userId).single(),
  ]);

  if (refRes.error) {
    console.error('Error fetching referrals:', refRes.error);
    return { referrals: [], code: null, totalEarnings: 0 };
  }

  return {
    referrals: refRes.data.map(transformReferral),
    code: userRes.data?.referral_code,
    totalEarnings: userRes.data?.total_referral_earnings || 0,
  };
}

/**
 * Fetch a single job by ID for Job Detail page
 */
export async function fetchJobById(jobId) {
  const [{ data, error }, { data: reqAssessments }] = await Promise.all([
    supabase.from('job').select('*').eq('id', jobId).single(),
    supabase.from('job_required_assessment').select('job_id, assessment_definition_id, is_required').eq('job_id', jobId),
  ]);

  if (error) {
    console.error('Error fetching job:', error);
    return null;
  }

  // Attach required assessments from junction table
  data._requiredAssessments = (reqAssessments || []).map(r => ({
    assessmentDefinitionId: r.assessment_definition_id,
    isRequired: r.is_required,
  }));

  return transformJob(data);
}

/**
 * Fetch saved job IDs for current user
 */
export async function fetchSavedJobIds() {
  const userId = getAuthUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('individual')
    .select('saved_job_ids')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching saved jobs:', error);
    return [];
  }

  return data?.saved_job_ids || [];
}

/**
 * Fetch full job objects for saved jobs (regardless of job status — a saved Draft job still shows)
 */
export async function fetchSavedJobs() {
  // First get saved IDs
  const savedIds = await fetchSavedJobIds();
  if (!savedIds.length) return [];

  // Then fetch full job data for those IDs (no status filter)
  const { data, error } = await supabase
    .from('job')
    .select('*')
    .in('id', savedIds);

  if (error) {
    console.error('Error fetching saved jobs:', error);
    return [];
  }

  const rows = data || [];
  const liveIds = new Set(rows.map(row => row.id));
  const cleanedIds = savedIds.filter(id => liveIds.has(id));

  if (cleanedIds.length !== savedIds.length) {
    const { error: cleanupError } = await supabase
      .from('individual')
      .update({ saved_job_ids: cleanedIds })
      .eq('id', getAuthUserId());

    if (cleanupError) {
      console.error('Error cleaning stale saved jobs:', cleanupError);
    }
  }

  const rowById = new Map(rows.map(row => [row.id, row]));
  return cleanedIds
    .map(id => rowById.get(id))
    .filter(Boolean)
    .map(transformJob);
}

// ============================================================
// WRITE FUNCTIONS
// ============================================================

/**
 * Apply to a job
 */
export async function submitApplication(jobId, source = 'StandardApply', steps = {}) {
  let userId;
  try {
    userId = requireAuthUserId('Submitting an application');
  } catch (error) {
    console.error(error.message);
    return null;
  }

  const { data, error } = await supabase
    .from('application')
    .insert({
      job_id: jobId,
      applicant_id: userId,
      source,
      stage: source === 'InstantMatch' ? 'Accepted' : 'Applied',
      resume_submitted: steps.resume ?? true,
      domain_assessment_passed: steps.domainAssessment ?? false,
      skills_evaluation_passed: steps.skillsEvaluation ?? false,
      work_auth_verified: steps.workAuth ?? true,
      completed_steps: Object.values(steps).filter(Boolean).length,
      org_id: (await supabase.auth.getUser()).data?.user?.app_metadata?.org_id || null,
    })
    .select('*, job(title, domain, tier, base_rate, client_display_name)')
    .single();

  if (error) {
    console.error('Error submitting application:', error);
    return null;
  }

  return transformApplication(data);
}

/**
 * Toggle save/unsave a job
 */
export async function toggleSaveJob(jobId, currentSavedIds) {
  let userId;
  try {
    userId = requireAuthUserId('Saving a job');
  } catch (error) {
    console.error(error.message);
    return currentSavedIds;
  }

  const isSaved = currentSavedIds.includes(jobId);
  const newIds = isSaved
    ? currentSavedIds.filter(id => id !== jobId)
    : [...currentSavedIds, jobId];

  const { error } = await supabase
    .from('individual')
    .update({ saved_job_ids: newIds })
    .eq('id', userId);

  if (error) {
    console.error('Error toggling save:', error);
    return currentSavedIds;
  }

  return newIds;
}

// ============================================================
// HELPERS
// ============================================================

function getRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  return `${Math.floor(diffDays / 7)} weeks ago`;
}

function mapStage(dbStage) {
  const map = {
    'Applied': 'applied',
    'Under Review': 'under_review',
    'Assessment': 'assessment',
    'Skills Evaluation': 'skills_evaluation',
    'Accepted': 'accepted',
    'Not Selected': 'not_selected',
    'Withdrawn': 'withdrawn',
  };
  return map[dbStage] || dbStage?.toLowerCase();
}
